import { assertDefined, assertNonNull, stop, stopAll, type StopHandle, taggedLogger } from '@/util';
import {
    CanvasTexture,
    DoubleSide,
    LinearFilter,
    LinearMipMapLinearFilter,
    MathUtils,
    Mesh,
    MeshBasicMaterial,
    PlaneGeometry,
    RepeatWrapping,
    type Texture,
    TextureLoader,
} from 'three';
import { computed, markRaw, type Ref, watchEffect } from 'vue';
import type { ApiCaseCatStack } from '@/api/catStacks';
import { loadImageAsUrl } from '@/api/loadImage';
import type { Url } from '@/domain/types';
import { watchImmediate } from '@vueuse/core';
import { useAppErrorStore } from '@/stores/appErrorStore';
import type { OverlayStore } from '@/views/dicoms/overlay/overlayStore';
import type { OverlaySceneContext } from '@/views/dicoms/overlay/scene/overlaySceneContext';
import type { OverlayImageNode } from '@/views/dicoms/overlay/scene/overlayImageNode';
import type { SceneContext } from '@/lib/threeScene/SceneContext';
import { updateObject } from '@/lib/threeScene/nodes/object';

const log = taggedLogger('overlay-view');

export function updateOverlayCatstackImage(
    context: OverlaySceneContext,
    store: OverlayStore,
    node: OverlayImageNode,
): StopHandle {
    return stopAll(
        _updateImagePlane(context, node),
        _updateSize(store, node),
        _updatePosition(store, node),
        _updateVisibility(store, node),
        _updateImage(store, node),
        _updateSourceCanvas(store, node),
    );
}

function _updatePosition(store: OverlayStore, node: OverlayImageNode): StopHandle {
    return watchEffect(() => {
        if (store.catstacksData !== null) {
            const data = store.catstacksData[node.planeId];
            if (data && data.axis) {
                const sliceSpacing = data.axis.length / (data.catstack.count - 1);
                const offset = sliceSpacing * store.catstacks[node.planeId].sliceIndex;
                const newPosition = data.axis.direction
                    .clone()
                    .multiplyScalar(offset)
                    .add(data.axis.origin);

                node.transform.setPosition(newPosition);

                console.log(
                    `${node.planeId} plane positioned at:`,
                    newPosition,
                    `offset: ${offset}`,
                    `visible: ${node.visible}`,
                    `geometry: ${node.width}x${node.height}`,
                );
            }
        }
    });
}

function _updateSize(store: OverlayStore, node: OverlayImageNode): StopHandle {
    return watchImmediate(
        () =>
            store.catstacksData && store.catstacksData[node.planeId]
                ? store.catstacksData[node.planeId].catstack.world
                : null,
        (size) => {
            if (size) {
                node.width = size.width;
                node.height = size.height;
            }
        },
    );
}

function _updateVisibility(store: OverlayStore, node: OverlayImageNode): StopHandle {
    return watchImmediate(
        () => (store.catstacks ? store.catstacks[node.planeId]?.visible : false),
        (visible) => {
            console.log(`${node.planeId} visibility changed to: ${visible}`);
            node.visible = !!visible;
        },
    );
}

function _updateImage(store: OverlayStore, node: OverlayImageNode): StopHandle {
    let loadingImage: StopHandle | null = null;
    return watchImmediate(
        () =>
            !store.isLoading && store.catstacksData && store.catstacksData[node.planeId]
                ? store.catstacksData[node.planeId].catstack.image_url
                : null,
        (sourceUrl) => {
            stop(loadingImage);
            if (sourceUrl !== null) {
                loadingImage = _loadImage(node, sourceUrl);
            } else {
                node.image = null;
            }
        },
    );
}

function _loadImage(node: OverlayImageNode, sourceUrl: Url): StopHandle {
    const aborter = new AbortController();
    const signal = aborter.signal;

    useAppErrorStore().catchErrors(async () => {
        try {
            log.info('Loading image for %s overlay catstack\n  from %s', node.planeId, sourceUrl);
            const data = await loadImageAsUrl(sourceUrl, { signal });
            const image = new Image();
            image.src = assertDefined(data, 'catstack-image-url');

            await new Promise<void>((resolve, reject) => {
                let onLoaded: (() => void) | null = null;
                onLoaded = () => {
                    log.info(
                        'Loaded image for %s overlay catstack\n  from %s',
                        node.planeId,
                        sourceUrl,
                    );
                    image.removeEventListener('load', assertNonNull(onLoaded, 'onLoaded'));
                    node.image = markRaw(image);
                    resolve();
                };
                signal.addEventListener('abort', () => {
                    log.debug(
                        'Aborting load of image for %s overlay catstack\n  from %s',
                        node.planeId,
                        sourceUrl,
                    );
                    image.removeEventListener('load', assertNonNull(onLoaded, 'onLoaded'));
                    reject(signal.reason);
                });
                image.addEventListener('load', onLoaded, false);
            });
        } catch (error) {
            if (signal.aborted) {
                log.debug('Aborted loading image for %s from %s', node.planeId ?? '?', sourceUrl);
            } else {
                throw error;
            }
        }
    });

    return () => aborter.abort();
}

export function _updateSourceCanvas(store: OverlayStore, node: OverlayImageNode): StopHandle {
    let update: StopHandle | null = null;
    return stopAll(
        watchImmediate(
            () => !store.isLoading && store.catstacksData && node.image,
            (loaded) => {
                stop(update);

                if (!loaded) {
                    node.source = null;
                    return;
                }

                const catstack = assertNonNull(store.catstacksData, 'cat-stack-data')[node.planeId]
                    .catstack;
                const canvas = document.createElement('canvas');

                // canvas width and height require sizes to be power of two
                canvas.width = MathUtils.ceilPowerOfTwo(catstack.world.width);
                canvas.height = MathUtils.ceilPowerOfTwo(catstack.world.height);
                update = watchImmediate(
                    () => store.catstacks[node.planeId].sliceIndex,
                    (value) => _redrawCanvas(node, catstack, canvas, value),
                );
                node.source = canvas;
            },
        ),
        () => stop(update),
    );
}

type _Canvas2dOffset = {
    x: number;
    y: number;
};

function _canvasOffsetFromSliceIndex(
    catStack: ApiCaseCatStack,
    sliceIndex: number,
): _Canvas2dOffset {
    const sliceSize = catStack.slice;
    const image = catStack.image;

    const total = catStack.count;
    const sliceWidth = sliceSize.width;
    const sliceHeight = sliceSize.height;
    const rows = image.height / sliceSize.height;

    const currentColumn = Math.ceil((total - sliceIndex + 1) / rows);
    const currentRow = total - sliceIndex + 1 - rows * (currentColumn - 1);

    const slicePositionX = -sliceWidth * (currentColumn - 1);
    const slicePositionY = -sliceHeight * (currentRow - 1);

    // Avoid formatting unused debug messages
    if (log.enabledFor('debug')) {
        log.debug(
            [
                'Calculated canvas offset from slice: ',
                `currentSlice: ${sliceIndex}`,
                `rows: ${rows}`,
                `currentColumn: ${currentColumn}`,
                `currentRow: ${currentRow}`,
                `slicePositionX: ${slicePositionX}`,
                `slicePositionY: ${slicePositionY}`,
                `x: ${slicePositionX / -1}`,
                `y: ${slicePositionY / -1}`,
            ].join('\n  '),
        );
    }

    return {
        x: slicePositionX / -1,
        y: slicePositionY / -1,
    };
}

function _redrawCanvas(
    node: OverlayImageNode,
    catstack: ApiCaseCatStack,
    canvas: HTMLCanvasElement,
    sliceIndex: number,
): void {
    const canvasOffset = _canvasOffsetFromSliceIndex(catstack, sliceIndex);
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

    log.debug(
        [
            `Redrawing canvas for ${node.planeId} overlay catstack. Parameters:`,
            `offsetX: ${canvasOffset.x}`,
            `offsetY: ${canvasOffset.y}`,
            `sliceWidth: ${catstack.slice.width}`,
            `sliceHeight: ${catstack.slice.height}`,
            `imgWidth: ${canvas.width}`,
            `imgHeight: ${canvas.height}`,
        ].join('\n  '),
    );

    ctx.drawImage(
        assertNonNull(node.image, 'cat-stack image'),
        canvasOffset.x,
        canvasOffset.y,
        catstack.slice.width,
        catstack.slice.height,
        0,
        0,
        canvas.width, // canvas size
        canvas.height,
    );

    // Increment sourceVersion to trigger a material update
    node.sourceVersion += 1;
}

function _updateImagePlane(context: SceneContext, node: OverlayImageNode): StopHandle {
    const mesh = new Mesh();
    const material = new MeshBasicMaterial({
        side: DoubleSide,
    });
    mesh.material = material;
    let geometry: PlaneGeometry | null = null;
    return stopAll(
        updateObject(context, node, mesh),
        watchImmediate(
            () => [node.width, node.height],
            ([width, height]) => {
                if (geometry) {
                    geometry.dispose();
                    geometry = null;
                }
                if (width > 0 && height > 0) {
                    geometry = new PlaneGeometry(width, height);
                    mesh.geometry = geometry;

                    if (material.map) {
                        material.needsUpdate = true;
                    }
                }
            },
        ),
        _updateTexture(node, material),
    );
}

/** Update material texture (aka map) from the source */
function _updateTexture(node: OverlayImageNode, material: MeshBasicMaterial): StopHandle {
    let textureUpdate: StopHandle | null = null;
    return stopAll(
        watchImmediate(
            () => node.source,
            (source) => {
                stop(textureUpdate);

                if (source === null) {
                    material.map = null;
                    material.needsUpdate = true;
                    textureUpdate = null;
                    return;
                } else if (source instanceof HTMLCanvasElement) {
                    textureUpdate = _updateCanvasTexture(
                        source,
                        computed(() => node.sourceVersion),
                        material,
                    );
                } else {
                    textureUpdate = _updateUrlTexture(
                        source,
                        computed(() => node.sourceVersion),
                        material,
                    );
                }
            },
        ),
        () => stop(textureUpdate),
    );
}

function _updateCanvasTexture(
    source: HTMLCanvasElement,
    sourceVersion: Ref<number>,
    material: MeshBasicMaterial,
): StopHandle {
    const texture = new CanvasTexture(source);
    // Filters
    texture.magFilter = LinearFilter;
    texture.minFilter = LinearMipMapLinearFilter;

    // Flip the image horizontally
    texture.wrapS = texture.wrapT = RepeatWrapping;

    material.map = texture;
    material.needsUpdate = true;

    return stopAll(
        watchImmediate(sourceVersion, () => {
            texture.needsUpdate = true;
        }),
        () => {
            texture.dispose();
            material.map = null;
            material.needsUpdate = true;
        },
    );
}

let _textureLoaderInstance: TextureLoader | null = null;

function _textureLoader(): TextureLoader {
    if (_textureLoaderInstance === null) {
        _textureLoaderInstance = new TextureLoader();
    }
    return _textureLoaderInstance;
}

function _updateUrlTexture(
    source: string,
    sourceVersion: Ref<number>,
    material: MeshBasicMaterial,
): StopHandle {
    const loader = _textureLoader();
    const controller = new AbortController();
    let texture: Texture | null = null;

    const disposeTexture = () => {
        if (texture !== null) {
            texture.dispose();
            texture = null;
        }
    };

    return stopAll(
        watchImmediate(sourceVersion, async () => {
            disposeTexture();
            try {
                const data = await loadImageAsUrl(source, { signal: controller.signal });
                if (!data) {
                    log.warning('Failed to load image from %s', source);
                    return;
                }
                texture = loader.load(data);
                material.map = texture;
                material.needsUpdate = true;
            } catch (error) {
                if (!controller.signal.aborted) {
                    throw error;
                }
            }
        }),
        () => {
            controller.abort();
            disposeTexture();
        },
    );
}
